import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient.web";
import { authCodeFor, authMessageFor } from "../lib/errorCodes";
import Turnstile, { type TurnstileHandle } from "../lib/Turnstile";
import BrandHeader from "../lib/BrandHeader";

/**
 * "Check your email", and a way to send the link again.
 *
 * ---------------------------------------------------------------------------
 * THERE IS NO "I'VE VERIFIED - CONTINUE" BUTTON, AND THERE MUST NOT BE ONE
 * (removed 2026-09-16, Phase 11.3).
 *
 * THE EMAIL LINK DOES THE CONTINUING. Signup asks GoTrue to send the link to
 * `/connect`, and auth-js's defaults (`flowType: 'implicit'`,
 * `detectSessionInUrl: true`, neither overridden in supabaseClient.web.ts) sign
 * that new tab in from the link itself. /connect then asks for a username if
 * there is none, and hands the session to the extension. Nobody needs to come
 * back to this page, so it says so, and says they can close it.
 *
 * THE BUTTON THAT WAS HERE COULD NOT WORK. It called getSession() and went on to
 * /connect if it found a verified user. That relied on the link's tab and this
 * one sharing a login through localStorage, which they did when it was built
 * (f46bb33). f860b9a turned `persistSession` off - the web app must never
 * refresh a session the extension holds - and auth-js then keeps the login in a
 * per-page memory store with no cross-tab channel (read in auth-js 2.89.0,
 * GoTrueClient constructor). Every route into this page arrives with that store
 * empty, so every press said "You are not logged in yet" to someone who had
 * just logged in. Bringing it back means sharing the login between tabs again,
 * which is the double-refresh that signs the extension out.
 *
 * "Go to connect" went with it: with no login in memory, /connect only
 * redirects to the login page. What stays is Log in, for the one case the link
 * cannot finish - opened on a phone or in a browser without TakeNote, where
 * /connect has no extension to hand the session to.
 * ---------------------------------------------------------------------------
 */
export default function VerifyEmailPage() {
  const sp = useMemo(() => new URLSearchParams(window.location.search), []);
  const emailFromQuery = sp.get("email") ?? "";

  const [email, setEmail] = useState(emailFromQuery);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // `/auth/v1/resend` is captcha-gated by the same global setting that gates
  // signup and login. Measured: without a token it returns `400 captcha_failed`,
  // so the button below cannot work without a widget on this page.
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const turnstile = useRef<TurnstileHandle | null>(null);

  // The same destination SignupPage gives the first email, so a resent link
  // behaves exactly like the original. Without it GoTrue falls back to
  // `site_url`, which reaches /connect only by way of the index redirect.
  const emailRedirectTo = useMemo(() => `${window.location.origin}/connect`, []);

  const canResend = !!captchaToken && !busy;

  async function resend() {
    setErr(null);
    setMsg(null);

    const e = email.trim();
    if (!e) {
      setErr("Email is required.");
      return;
    }

    if (!captchaToken) return;

    setBusy(true);
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: e,
      options: { captchaToken, emailRedirectTo },
    });
    setBusy(false);

    // Spent either way, so the next press needs a fresh challenge.
    turnstile.current?.reset();
    setCaptchaToken(null);

    // NOT BRANCHED ON THE REFUSAL, except for the two that say nothing about
    // the address (Phase 12's audit, measured on the running GoTrue): a resend
    // answers 200 for an unknown, a confirmed and a pending address alike, but a
    // SECOND resend is refused with over_email_send_rate_limit only for an
    // address that is registered and not yet confirmed. Showing that refusal -
    // this page used to show GoTrue's own sentence - told anyone which
    // addresses had signed up and not finished. ForgotPasswordPage made the
    // same choice for the same reason. The captcha is checked before any
    // lookup, and a request that never got an answer says nothing either.
    if (error) {
      const code = authCodeFor(error);
      if (code === "CAPTCHA_FAILED" || code === "NETWORK") {
        setErr(authMessageFor(code));
        return;
      }
    }

    setMsg("Verification email sent. Please check your inbox.");
  }

  return (
    <div className="min-h-screen grid place-items-center p-4">
      <div className="w-full max-w-md rounded-2xl bg-surface shadow p-6 space-y-4">
        <BrandHeader className="" />
        <h1 className="text-xl font-semibold">Verify your email</h1>

        <div className="text-sm text-fg-body">
          Click the link we emailed you to verify your address. It opens a new tab and carries on
          from there, so you can close this one.
        </div>

        {/* A form, so Enter in the address resends (Phase 12.5) - it was the one
            page with a single box and a button that Enter did nothing on. The
            button stays disabled until the human check passes, and a form with
            a disabled button does not submit on Enter either. */}
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (canResend) void resend();
          }}
        >
        <div>
          <label className="text-sm font-medium" htmlFor="email">Email</label>
          <input
            id="email"
            className="mt-1 w-full rounded-xl border border-line-control px-3 py-2"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </div>

        {err && (
          <div className="rounded-xl border border-danger-line bg-danger-bg p-3 text-sm text-danger">
            {err}
          </div>
        )}

        {msg && (
          <div className="rounded-xl border border-success-line bg-success-bg p-3 text-sm text-success">
            {msg}
          </div>
        )}

        <Turnstile ref={turnstile} onToken={setCaptchaToken} />

        <button
          type="submit"
          disabled={!canResend}
          className="w-full rounded-xl bg-primary text-on-primary py-2 disabled:opacity-60"
        >
          {busy ? "Sending…" : "Resend verification email"}
        </button>
        </form>

        {/* No standing hint under the button (11.3) - see Turnstile.tsx. */}

        {/* The one case the email link cannot finish. "Log in", not "Back to
            login": one spelling of one destination (11.3). LoginPage returns
            to /connect when no returnTo is given. */}
        <div className="text-sm text-fg-soft">
          Verified on another device?{" "}
          <Link className="text-link hover:underline" to="/auth/login">
            Log in
          </Link>
        </div>

        {/* Sign-up sends an address that already has an account here too, and
            emails it nothing - the server answers it as if it were new, so the
            sign-up page cannot say which addresses have accounts (SignupPage,
            REFUSED). This line is for the person who forgot they had one. It
            leads with "Didn't receive an email?" because people also arrive
            from Log in and Connect, where an account is not in question. */}
        <div className="text-sm text-fg-soft">
          Didn't receive an email? If this address already has an account,{" "}
          <Link className="text-link hover:underline" to="/auth/login">
            log in
          </Link>{" "}
          or{" "}
          <Link className="text-link hover:underline" to="/auth/forgot">
            reset your password
          </Link>
          .
        </div>
      </div>
    </div>
  );
}
